import React, { useState, useEffect } from "react";
import { backendApi } from "./api/backendApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Clock, Users, Download, Filter, CheckCircle, XCircle, AlertCircle,
  BarChart3, PieChart as PieChartIcon, Search,
} from "lucide-react";
import { format } from "date-fns";

// ---------------------------------------------------------------------
//  THEME TOKENS — matches the flat editorial surface used across HRMS
//  (kept in sync with the tokens in EmployeeDashboard.jsx)
// ---------------------------------------------------------------------
const T = {
  green: '#1A3C2B',
  greenDeep: '#122B1F',
  greenTint: '#EDF3EF',
  bg: '#F6F6F3',
  card: '#FFFFFF',
  line: '#E7E6E0',
  lineSoft: '#F0EFEA',
  ink: '#15181C',
  body: '#3F4650',
  muted: '#6B7280',
  faint: '#9CA3AF',
};

const SERIES = {
  present: '#2F7D4F',
  absent: '#D9534F',
  late: '#E0A33E',
  half: '#7E6BE0',
};

// ---------------------------------------------------------------------
//  PRIMITIVES (mirrors the primitives used on the Dashboard page)
// ---------------------------------------------------------------------
const CardTitle = ({ icon: Icon, children, note }) => (
  <div className="flex items-center gap-2 min-w-0">
    {Icon && <Icon className="w-[15px] h-[15px] flex-shrink-0" style={{ color: T.green }} strokeWidth={2} />}
    <h3 className="hrms-label truncate">{children}</h3>
    {note && <span className="hrms-note truncate">{note}</span>}
  </div>
);

const Panel = ({ title, icon, note, right, className = '', bodyClass = '', padded = true, children }) => (
  <section className={`hrms-card flex flex-col ${className}`}>
    {(title || right) && (
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
        <CardTitle icon={icon} note={note}>{title}</CardTitle>
        {right}
      </div>
    )}
    <div className={`${padded ? 'px-5 pb-5' : ''} flex-1 min-w-0 ${bodyClass}`}>{children}</div>
  </section>
);

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const tone =
    s.includes('present') || s.includes('wfh')
      ? { bg: '#E8F3EC', fg: '#2F7D4F' }
      : s.includes('late')
        ? { bg: '#FBF1E1', fg: '#B9822E' }
        : s.includes('absent')
          ? { bg: '#FCEAEA', fg: '#C0413D' }
          : { bg: '#F3F2ED', fg: '#6B7280' };
  return (
    <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap"
      style={{ background: tone.bg, color: tone.fg }}>
      {status}
    </span>
  );
};

// Flat stat tile — replaces the old gradient/shadow stat cards.
const StatTile = ({ icon: Icon, label, value, tint, fg }) => (
  <div className="hrms-card flex items-center gap-4 p-5">
    <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: tint, color: fg }}>
      <Icon className="w-5 h-5" strokeWidth={1.8} />
    </span>
    <div className="min-w-0">
      <p className="text-[22px] font-extrabold leading-tight" style={{ color: T.ink }}>{value}</p>
      <p className="hrms-label mt-1">{label}</p>
    </div>
  </div>
);

export default function AttendancePage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [user, setUser] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [hoverStatus, setHoverStatus] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await backendApi.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error("Error loading user:", error);
      }
    };
    loadUser();
  }, []);

  const { data: attendanceRecords = [] } = useQuery({
    queryKey: ['attendance'],
    queryFn: () => backendApi.entities.Attendance.list(),
    initialData: [],
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => backendApi.entities.Employee.list(),
    initialData: [],
  });

  const checkInMutation = useMutation({
    mutationFn: async (data) => {
      return await backendApi.entities.Attendance.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });

  const handleExport = () => {
    if (attendanceRecords.length === 0) {
      return;
    }
    const headers = ["Employee", "Date", "Check In", "Check Out", "Hours", "Status", "Location"];
    const rows = attendanceRecords.map((r) => [
      r.employee_name || "",
      r.date || "",
      r.check_in || "",
      r.check_out || "",
      r.hours_worked || "",
      r.status || "",
      r.location || "",
    ]);
    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  const handleCheckIn = async () => {
    if (!user) return;

    const now = new Date();
    const checkInData = {
      employee_id: user.id,
      employee_name: user.full_name,
      date: format(now, 'yyyy-MM-dd'),
      check_in: format(now, 'HH:mm:ss'),
      status: now.getHours() > 9 ? "Late" : "Present",
      location: "Office"
    };

    checkInMutation.mutate(checkInData);
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayAttendance = attendanceRecords.filter(r => r.date === today);
  const totalEmployees = employees.length;
  const presentToday = todayAttendance.filter(r => r.status === "Present" || r.status === "Late").length;
  const absentToday = totalEmployees - presentToday;
  const lateComers = todayAttendance.filter(r => r.status === "Late").length;

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayRecords = attendanceRecords.filter(r => r.date === dateStr);
    return {
      name: format(date, 'EEE'),
      present: dayRecords.filter(r => r.status === "Present" || r.status === "Late").length,
      absent: totalEmployees - dayRecords.filter(r => r.status === "Present" || r.status === "Late").length
    };
  });

  const statusData = [
    { name: 'Present', value: todayAttendance.filter(r => r.status === "Present").length, color: SERIES.present },
    { name: 'Late', value: lateComers, color: SERIES.late },
    { name: 'Absent', value: absentToday, color: SERIES.absent },
    { name: 'WFH', value: todayAttendance.filter(r => r.status === "WFH").length, color: SERIES.half },
  ];

  const filteredAttendance = todayAttendance.filter((record) => {
    const matchesSearch = record.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || record.status === statusFilter;
    return (!searchTerm || matchesSearch) && matchesStatus;
  });

  // ---- daily bar chart scale -------------------------------------------
  const dayPeak = Math.max(1, totalEmployees);
  const dayStep = Math.ceil(dayPeak / 3) || 1;
  const dayTop = dayStep * 3;
  const dayTicks = [dayTop, dayStep * 2, dayStep, 0];

  // ---- status donut ------------------------------------------------------
  const statusTotal = statusData.reduce((sum, d) => sum + d.value, 0) || 1;
  const CIRC = 2 * Math.PI * 42;
  let acc = 0;

  return (
    <div className="p-6 md:p-8" style={{ background: T.bg, minHeight: '100%' }}>
      {/* Header row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-[19px] font-bold tracking-tight" style={{ color: T.ink }}>Attendance Overview</h2>
          <p className="text-[12px] mt-0.5" style={{ color: T.muted }}>Track and manage employee attendance</p>
        </div>
        <div className="flex gap-2.5 flex-wrap">
          <div className="flex items-center gap-2 h-9 px-3 rounded-lg"
            style={{ border: `1px solid ${T.line}`, background: T.card }}>
            <Filter className="w-3.5 h-3.5 flex-shrink-0" style={{ color: T.muted }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-full bg-transparent text-[12.5px] font-medium focus:outline-none"
              style={{ color: T.body }}
            >
              <option value="All">All statuses</option>
              <option value="Present">Present</option>
              <option value="Late">Late</option>
              <option value="WFH">WFH</option>
            </select>
          </div>
          <button onClick={handleExport} className="hrms-btn-ghost inline-flex items-center gap-2">
            <Download className="w-3.5 h-3.5" strokeWidth={2} />
            Export CSV
          </button>
          <button onClick={handleCheckIn} className="hrms-btn-primary inline-flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" strokeWidth={2} />
            Check In Now
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatTile icon={Users} label="Total Employees" value={totalEmployees} tint={T.greenTint} fg={T.green} />
        <StatTile icon={CheckCircle} label="Present Today" value={presentToday} tint="#E8F3EC" fg="#2F7D4F" />
        <StatTile icon={XCircle} label="Absent Today" value={absentToday} tint="#FCEAEA" fg="#C0413D" />
        <StatTile icon={AlertCircle} label="Late Comers" value={lateComers} tint="#FBF1E1" fg="#B9822E" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <Panel title="Daily Attendance" icon={BarChart3} note="(Last 7 Days)">
          <div className="flex gap-3 h-[180px]">
            <div className="flex flex-col justify-between py-1 text-[10px] font-medium text-right w-6 flex-shrink-0"
              style={{ color: T.faint }}>
              {dayTicks.map((t) => <span key={t}>{t}</span>)}
            </div>
            <div className="relative flex-1 min-w-0">
              {dayTicks.map((t, i) => (
                <span key={t} className="absolute left-0 right-0 border-t"
                  style={{ borderColor: T.lineSoft, top: `${(i / 3) * 100}%` }} />
              ))}
              <div className="relative h-full flex items-end justify-around gap-2 pb-px">
                {last7Days.map((d, i) => (
                  <div key={d.name} className="relative flex-1 flex flex-col items-center justify-end h-full cursor-default"
                    onMouseEnter={() => setHoverDay(i)}
                    onMouseLeave={() => setHoverDay((prev) => (prev === i ? null : prev))}
                  >
                    {hoverDay === i && (
                      <div className="absolute bottom-full mb-2 z-10 px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                        style={{ background: T.ink, border: `1px solid ${T.line}` }}>
                        <p className="text-[11px] font-bold text-white mb-1">{d.name}</p>
                        <p className="text-[10.5px] font-medium flex items-center gap-1.5" style={{ color: '#B9E4C6' }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: SERIES.present }} />
                          Present: {d.present}
                        </p>
                        <p className="text-[10.5px] font-medium flex items-center gap-1.5" style={{ color: '#F3C6C4' }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: SERIES.absent }} />
                          Absent: {d.absent}
                        </p>
                      </div>
                    )}
                    <div className="flex items-end justify-center gap-[3px] w-full h-full">
                      <div className="w-[9px] rounded-t-[3px] transition-all"
                        style={{
                          height: `${Math.max(d.present > 0 ? 4 : 2, (d.present / dayTop) * 100)}%`,
                          background: SERIES.present,
                          opacity: d.present > 0 ? 1 : 0.35,
                          filter: hoverDay === i ? 'brightness(1.15)' : 'none',
                          boxShadow: hoverDay === i ? '0 0 0 1px rgba(0,0,0,0.08)' : 'none',
                        }} />
                      <div className="w-[9px] rounded-t-[3px] transition-all"
                        style={{
                          height: `${Math.max(d.absent > 0 ? 4 : 2, (d.absent / dayTop) * 100)}%`,
                          background: SERIES.absent,
                          opacity: d.absent > 0 ? 1 : 0.35,
                          filter: hoverDay === i ? 'brightness(1.15)' : 'none',
                          boxShadow: hoverDay === i ? '0 0 0 1px rgba(0,0,0,0.08)' : 'none',
                        }} />
                    </div>
                    <span className="text-[10px] font-medium mt-2 transition-colors"
                      style={{ color: hoverDay === i ? T.ink : T.muted }}>{d.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 pl-9">
            <span className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: T.muted }}>
              <span className="w-2 h-2 rounded-full" style={{ background: SERIES.present }} />Present
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: T.muted }}>
              <span className="w-2 h-2 rounded-full" style={{ background: SERIES.absent }} />Absent
            </span>
          </div>
        </Panel>

        <Panel title="Attendance Status" icon={PieChartIcon} note="(Today)">
          <div className="flex items-center gap-5">
            <div className="relative w-[112px] h-[112px] flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="transparent" stroke={T.lineSoft} strokeWidth="13" />
                {statusData.map((item, i) => {
                  const dash = (item.value / statusTotal) * CIRC;
                  const isHover = hoverStatus === i;
                  const seg = (
                    <circle key={i} cx="50" cy="50" r="42" fill="transparent"
                      stroke={item.color} strokeWidth={isHover ? "16" : "13"} strokeLinecap="butt"
                      strokeDasharray={`${dash} ${CIRC}`} strokeDashoffset={`${-acc}`}
                      style={{
                        opacity: hoverStatus === null || isHover ? 1 : 0.35,
                        cursor: 'pointer',
                        transition: 'stroke-width 0.15s ease, opacity 0.15s ease',
                      }}
                      onMouseEnter={() => setHoverStatus(i)}
                      onMouseLeave={() => setHoverStatus((prev) => (prev === i ? null : prev))}
                    />
                  );
                  acc += dash;
                  return seg;
                })}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[18px] font-extrabold leading-none" style={{ color: T.ink }}>
                  {hoverStatus !== null ? statusData[hoverStatus].value : statusTotal}
                </span>
                <span className="text-[9.5px] font-medium mt-0.5 text-center px-2 leading-tight" style={{ color: T.muted }}>
                  {hoverStatus !== null ? statusData[hoverStatus].name : 'Total'}
                </span>
              </div>
            </div>
            <div className="flex-1 space-y-3 min-w-0">
              {statusData.map((item, i) => (
                <div key={item.name} className="flex items-center justify-between gap-3 cursor-default rounded-md px-1.5 -mx-1.5 py-0.5 transition-colors"
                  style={{ background: hoverStatus === i ? T.greenTint : 'transparent' }}
                  onMouseEnter={() => setHoverStatus(i)}
                  onMouseLeave={() => setHoverStatus((prev) => (prev === i ? null : prev))}
                >
                  <span className="flex items-center min-w-0">
                    <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ background: item.color }} />
                    <span className="text-[13px] truncate" style={{ color: T.body }}>{item.name}</span>
                  </span>
                  <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: T.ink }}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      {/* Today's attendance table */}
      <Panel
        title="Today's Attendance"
        icon={Clock}
        padded={false}
        right={
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.faint }} />
            <input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 h-9 pl-9 pr-3 rounded-lg text-[12.5px] focus:outline-none"
              style={{ border: `1px solid ${T.line}`, background: T.card, color: T.ink }}
            />
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ borderBottom: `1px solid ${T.line}` }}>
              <tr>
                <th className="px-5 py-3 text-left hrms-label">Employee</th>
                <th className="px-5 py-3 text-left hrms-label">Department</th>
                <th className="px-5 py-3 text-left hrms-label">Check In</th>
                <th className="px-5 py-3 text-left hrms-label">Check Out</th>
                <th className="px-5 py-3 text-left hrms-label">Hours</th>
                <th className="px-5 py-3 text-left hrms-label">Status</th>
                <th className="px-5 py-3 text-left hrms-label">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: T.lineSoft }}>
              {filteredAttendance.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-[13px]" style={{ color: T.faint }}>
                    No attendance records for today
                  </td>
                </tr>
              ) : filteredAttendance.map((record) => {
                const employee = employees.find(e => e.id === record.employee_id);
                return (
                  <tr key={record.id} className="transition-colors hover:bg-[#F9F9F6]">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <span className="hrms-avatar">{record.employee_name?.[0] || "U"}</span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold truncate" style={{ color: T.ink }}>{record.employee_name}</p>
                          <p className="text-[11px] truncate" style={{ color: T.muted }}>{employee?.position || "N/A"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{employee?.department || "N/A"}</td>
                    <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{record.check_in || "-"}</td>
                    <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{record.check_out || "-"}</td>
                    <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{record.hours_worked ? `${record.hours_worked}h` : "-"}</td>
                    <td className="px-5 py-3.5"><StatusPill status={record.status} /></td>
                    <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{record.location || "N/A"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}